/**
 * Normalises the `/t/{slug}` tenant entry point into the hash route the app actually routes on.
 *
 * The entry point is a path segment by decision — one origin, one certificate, one registered redirect
 * URI, and adding a tenant costs no DNS record and no Ingress change. The app, however, is served by a
 * `HashRouter`, which never sees the path: nginx falls back to `index.html`, the router finds an empty
 * hash and renders the dashboard route, and a link that was supposed to start a tenant-scoped sign-in
 * silently starts a generic one instead.
 *
 * So the path is translated to the equivalent hash route before the router mounts. This is done here
 * rather than as an nginx rewrite so it holds in local development too, where there is no nginx.
 *
 * `/callback` needs no such treatment: `lib/auth.ts` reads `window.location.pathname` directly rather
 * than going through the router.
 */

/** `/t/{slug}`, with an optional trailing slash and nothing after it. */
const TenantEntryPath = /^\/t\/([^/?#]+)\/?$/

/**
 * The URL this location should be replaced with, or `null` when it is already routable.
 *
 * @param location the current location — `pathname` and `search` are read; taken as a parameter so this
 * is testable without a browser.
 */
export function tenantEntryRedirect(location: Pick<Location, 'pathname' | 'search'>): string | null {
  const match = TenantEntryPath.exec(location.pathname)
  if (match === null) {
    return null
  }

  const slug = match[1]
  // Re-encoded rather than passed through: the slug reaches the IdP as a query parameter, and a raw
  // value here would let a crafted link inject one of its own.
  return `/${location.search}#/t/${encodeURIComponent(decodeURIComponent(slug))}`
}

/**
 * Rewrites the address bar when the current URL is a tenant entry path. Called before the router mounts.
 *
 * Replaces rather than pushes, so the un-routable path does not sit in the back button.
 */
export function normaliseTenantEntryPath(): void {
  const target = tenantEntryRedirect(window.location)
  if (target !== null) {
    window.history.replaceState({}, '', target)
  }
}
